import { useMemo, useState } from "react";
import { SorterResult } from "antd5/es/table/interface";
import { startCase } from "lodash";
import { DateTime } from "luxon";
import useDeepCompareEffect from "use-deep-compare-effect";

import { RouterTextLink } from "components/actions/RouterLink";
import { Currency } from "components/app_layout/Typography";
import { commonTableColumns } from "lib/core_components/Table/ColumnTypes";
import { isQueryableSpendFilter, SpendFilters } from "lib/hooks/api/spend/spendFilters";
import { SpendTransaction, useSpendTransactions } from "lib/hooks/api/spend/useSpendTransactions";
import { TableKey, useTablePageSize } from "lib/hooks/useTablePageSize";
import { TypedColumnProps } from "lib/search/SearchTable";
import { SpendSortOrder, SpendTransactionOrderBy } from "lib/types/graphQLEnums";
import { TRANSACTION_COLUMN_FIELDS, TransactionColumn } from "../columns";
import { TransactionTableRow } from "../config";

const DEFAULT_COLUMN_SIZE = { large: 240, small: 160, medium: 160, xlarge: 300 };

/** The columns the picker knows about. Anything else is a per-file descriptive column. */
const PICKABLE_COLUMN_KEYS = new Set<string>(TRANSACTION_COLUMN_FIELDS);

/** What the rows are ordered by: newest first until someone picks a column. */
export type TransactionSort = { orderBy: SpendTransactionOrderBy; sortOrder: SpendSortOrder };

export const DEFAULT_TRANSACTION_SORT: TransactionSort = {
  orderBy: SpendTransactionOrderBy.Date,
  sortOrder: SpendSortOrder.Desc,
};

// The columns the server can order by, keyed by the column they sit on.
const SORTABLE_COLUMNS: Record<string, SpendTransactionOrderBy> = {
  date: SpendTransactionOrderBy.Date,
  amount: SpendTransactionOrderBy.Amount,
};

// "descend" again after "ascend", so a third click flips back rather than clearing the sort.
const SORT_DIRECTIONS: ("descend" | "ascend")[] = ["descend", "ascend", "descend"];

/**
 * Nothing indexes amount, so a value sort reads every matching row: fine for one buyer and
 * one supplier, but it times out on wider searches. Everywhere else stays newest first.
 */
export function canSortByValue(filters: SpendFilters): boolean {
  return filters.buyerGuids?.length === 1 && filters.supplierGuids?.length === 1;
}

/**
 * The sort the query can actually run: a value sort from a link or an older view falls back to
 * newest first when the filters are too wide for it.
 */
export function effectiveTransactionSort(
  sort: TransactionSort,
  filters: SpendFilters,
): TransactionSort {
  return sort.orderBy === SpendTransactionOrderBy.Amount && !canSortByValue(filters)
    ? DEFAULT_TRANSACTION_SORT
    : sort;
}

/**
 * Reads the transaction sort out of antd's sorter callback. Returns undefined for a column the
 * server can't order by, or when antd reports the sort being cleared.
 */
export function transactionSortFromAntSorter<T>(
  sorter: SorterResult<T> | SorterResult<T>[],
): TransactionSort | undefined {
  const single = Array.isArray(sorter) ? sorter[0] : sorter;
  const orderBy = SORTABLE_COLUMNS[String(single?.columnKey)];
  if (!orderBy || !single?.order) {
    return undefined;
  }
  return {
    orderBy,
    sortOrder: single.order === "ascend" ? SpendSortOrder.Asc : SpendSortOrder.Desc,
  };
}

function antSortOrder(sort: TransactionSort, orderBy: SpendTransactionOrderBy) {
  if (sort.orderBy !== orderBy) {
    return null;
  }
  return sort.sortOrder === SpendSortOrder.Asc ? ("ascend" as const) : ("descend" as const);
}

export type UseTransactionsTable = {
  data: TransactionTableRow[];
  isLoading: boolean;
  isError: boolean;
  columns: TypedColumnProps<TransactionTableRow>[];
  rowKey: (r: TransactionTableRow) => string;
  pagination: {
    current: number;
    pageSize: number;
    total?: number;
    onChange: (page: number, pageSize?: number) => void;
  };
  /** The order the rows are in: the caller's, unless the filters are too wide for a value sort. */
  sort: TransactionSort;
  /** Pass to the table's onChange: a click on a sortable header reorders every page, not just this one. */
  onSortChange: (
    sorter: SorterResult<TransactionTableRow> | SorterResult<TransactionTableRow>[],
  ) => void;
};

function sortableColumn(
  column: TypedColumnProps<TransactionTableRow>,
  sort: TransactionSort,
  valueSortable: boolean,
): TypedColumnProps<TransactionTableRow> {
  const orderBy = SORTABLE_COLUMNS[String(column.key)];
  if (!orderBy || (orderBy === SpendTransactionOrderBy.Amount && !valueSortable)) {
    return column;
  }
  return {
    ...column,
    // Ordered by the server, so the table only draws the arrow.
    sorter: true,
    sortDirections: SORT_DIRECTIONS,
    sortOrder: antSortOrder(sort, orderBy),
  };
}

/**
 * Each row's descriptive fields, keyed by type. A file can carry the same type more than
 * once on a row, so repeats become `category_2`, `category_3` and so on; the second value
 * reports the most of each type any row has, which decides how many columns to draw.
 */
function withDescriptiveColumns(rows: SpendTransaction[]) {
  const maxPerType: Record<string, number> = {};

  const data = rows.map((row) => {
    const seenOnRow: Record<string, number> = {};
    const descriptiveColumns = row.descriptiveFields?.reduce<Record<string, string>>(
      (fields, field) => {
        if (!field.type) {
          return fields;
        }
        const instance = (seenOnRow[field.type] || 0) + 1;
        seenOnRow[field.type] = instance;
        fields[instance > 1 ? `${field.type}_${instance}` : field.type] = field.data;
        maxPerType[field.type] = Math.max(maxPerType[field.type] || 0, instance);
        return fields;
      },
      {},
    );
    return { ...row, descriptiveColumns };
  });

  return { data, maxPerType };
}

function descriptiveColumnsFor(
  maxPerType: Record<string, number>,
): TypedColumnProps<TransactionTableRow>[] {
  return Object.entries(maxPerType).flatMap(([type, max]) =>
    Array.from({ length: max }, (_, i) => {
      const key = i === 0 ? type : `${type}_${i + 1}`;
      return {
        title: startCase(key.replace("_", " ")),
        render: (record: TransactionTableRow) => record.descriptiveColumns?.[key],
        sizeConfig: DEFAULT_COLUMN_SIZE,
        ellipsis: true,
        key,
      };
    }),
  );
}

const TRANSACTION_COLUMNS: TypedColumnProps<TransactionTableRow>[] = [
  {
    ...commonTableColumns.titleColumn,
    title: "Buyer",
    render: (r) => (
      <RouterTextLink to="/buyers/$id" params={{ id: r.buyerGuid }}>
        {r.buyerName}
      </RouterTextLink>
    ),
    sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
    key: "buyer",
  },
  {
    ...commonTableColumns.titleColumn,
    title: "Supplier",
    render: (r) => {
      const name = r.supplierName ?? r.supplier;
      // Link to the resolved supplier profile; unresolved rows stay plain text.
      return r.supplierGuid ? (
        <RouterTextLink to="/suppliers/$id" params={{ id: r.supplierGuid }}>
          {name}
        </RouterTextLink>
      ) : (
        name
      );
    },
    sizeConfig: { large: 500, small: 200, medium: 400, xlarge: 600 },
    key: "supplier",
  },
  {
    ...commonTableColumns.valueColumn,
    title: "Transaction value",
    render: (r) => <Currency value={r.amount} />,
    sizeConfig: { large: 300, small: 200, medium: 300, xlarge: 400 },
    key: "amount",
  },
  {
    ...commonTableColumns.dateColumn,
    title: "Date",
    render: (r) => (r.date ? DateTime.fromISO(r.date).toFormat("MMM d, yyyy") : null),
    key: "date",
    sizeConfig: DEFAULT_COLUMN_SIZE,
  },
];

type Options = {
  tableKey?: TableKey;
  /** Held by the caller, so a page can keep it in the URL and a modal can keep it local. */
  sort: TransactionSort;
  onSortChange: (sort: TransactionSort) => void;
};

/**
 * The transactions behind the filters, a page at a time. Owned by the view that shows
 * them, so the page above never has to know about rows, counts or paging.
 */
export function useTransactionsTable(
  filters: SpendFilters,
  columnFields: TransactionColumn[],
  { tableKey = "spend-data-table", sort: requestedSort, onSortChange }: Options,
): UseTransactionsTable {
  const isEnabled = isQueryableSpendFilter(filters);
  const [pageSize, setPageSize] = useTablePageSize(tableKey);
  const [currentPage, setCurrentPage] = useState(1);
  const valueSortable = canSortByValue(filters);
  const sort = effectiveTransactionSort(requestedSort, filters);

  // A new search starts again from the first page. Compared by value, because callers
  // rebuild the filters object on every render.
  useDeepCompareEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const {
    data: response,
    isLoading,
    isError,
  } = useSpendTransactions(
    { filters, limit: pageSize, offset: (currentPage - 1) * pageSize, ...sort },
    { enabled: isEnabled },
  );

  return useMemo(() => {
    const { data, maxPerType } = withDescriptiveColumns(response?.results ?? []);

    // Chosen columns in the chosen order, then the file's own descriptive columns.
    const all = [
      ...TRANSACTION_COLUMNS.map((column) => sortableColumn(column, sort, valueSortable)),
      ...descriptiveColumnsFor(maxPerType),
    ];
    const chosen = columnFields
      .map((field) => all.find((c) => c.key === field))
      .filter((c): c is TypedColumnProps<TransactionTableRow> => c !== undefined);
    const extra = all.filter((c) => !PICKABLE_COLUMN_KEYS.has(String(c.key)));

    return {
      data,
      isLoading: isEnabled && isLoading,
      isError,
      columns: [...chosen, ...extra],
      rowKey: (r) => r.id,
      sort,
      pagination: {
        current: currentPage,
        pageSize,
        total: response?.totalResults,
        onChange: (page, newPageSize) => {
          if (newPageSize) setPageSize(newPageSize);
          setCurrentPage(page);
        },
      },
      onSortChange: (sorter) => {
        const next = transactionSortFromAntSorter(sorter);
        if (next && (next.orderBy !== sort.orderBy || next.sortOrder !== sort.sortOrder)) {
          onSortChange(next);
          // A new order starts again from the first page.
          setCurrentPage(1);
        }
      },
    };
  }, [
    response,
    isEnabled,
    isLoading,
    isError,
    columnFields,
    currentPage,
    pageSize,
    setPageSize,
    sort,
    valueSortable,
    onSortChange,
  ]);
}
